import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import axios from "axios";
import { 
    Alert, 
    Box, 
    Container, 
    Pagination, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow,
    Card,
    Typography,
    Chip,
    TextField,
    Button,
    InputAdornment,
    Tooltip,
    IconButton
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { isFeatureEnabled } from "../../utils/serverFeatures";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CircleIcon from "@mui/icons-material/Circle";

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

function OnlineUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [message, setMessage] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const onlineUsersSupported = isFeatureEnabled("onlineUsers");

    const fetchOnlineUsers = (isManual = false) => {
        if (!onlineUsersSupported) {
            setMessage("Server hiện tại chưa hỗ trợ endpoint /auth/online-users.");
            setUsers([]);
            setTotalPages(1);
            return;
        }

        if (isManual) setLoading(true);
        setMessage("");
        axios
            .get(`${process.env.REACT_APP_API_URL}/auth/online-users`, {
                params: { page, limit: 20 }
            })
            .then((res) => {
                setUsers(res.data.data || []);
                setTotalPages(res.data.totalPages || 1);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                setMessage(error?.response?.data?.message || "Không lấy được danh sách user online.");
            });
    };

    useEffect(() => {
        fetchOnlineUsers(true);
    }, [page]);

    useEffect(() => {
        if (!onlineUsersSupported) return;
        const timer = setInterval(() => {
            fetchOnlineUsers(false);
        }, 15000);
        return () => clearInterval(timer);
    }, [page, onlineUsersSupported]);

    const handleChangePage = (e, newPage) => {
        setPage(newPage);
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        const day = ("0" + d.getDate()).slice(-2);
        const month = ("0" + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        const hours = ("0" + d.getHours()).slice(-2);
        const minutes = ("0" + d.getMinutes()).slice(-2);
        const seconds = ("0" + d.getSeconds()).slice(-2);
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const filteredUsers = useMemo(() => {
        if (!searchFilter.trim()) return users;
        return users.filter(u => 
            (u.username || "").toLowerCase().includes(searchFilter.trim().toLowerCase())
        );
    }, [users, searchFilter]);

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    {loading && (
                        <div className="loading" style={{ maxWidth: "100%", zIndex: 9999 }}>
                            <div className="loader"></div>
                        </div>
                    )}
                    <Container maxWidth={false}>
                        {/* Page Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5, color: "#0f172a" }}>
                                <WifiTetheringIcon sx={{ fontSize: 32, color: "#10b981" }} /> Danh Sách User Online
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Chip 
                                    icon={<CircleIcon sx={{ fontSize: "12px !important", color: "#10b981 !important" }} />}
                                    label={`Trực tuyến: ${users.length} người`} 
                                    sx={{ 
                                        fontWeight: 700, 
                                        fontSize: "14px", 
                                        backgroundColor: "#ecfdf5", 
                                        color: "#047857",
                                        border: "1px solid #a7f3d0",
                                        py: 0.5 
                                    }} 
                                />
                                <Chip 
                                    label="Tự động làm mới 15s" 
                                    size="small"
                                    variant="outlined"
                                    sx={{ color: "#64748b", borderColor: "#cbd5e1" }}
                                />
                                <Tooltip title="Làm mới ngay">
                                    <IconButton 
                                        onClick={() => fetchOnlineUsers(true)}
                                        sx={{ backgroundColor: "#ffffff", border: "1px solid #cbd5e1", "&:hover": { backgroundColor: "#f8fafc" } }}
                                    >
                                        <RefreshIcon sx={{ color: "#3b82f6" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {message ? (
                            <Alert severity={onlineUsersSupported ? "error" : "warning"} sx={{ mb: 3, borderRadius: 2 }}>
                                {message}
                            </Alert>
                        ) : null}

                        {/* Filter Bar */}
                        <Card sx={{ p: 2.5, mb: 3, borderRadius: 2, boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)", backgroundColor: "#ffffff" }}>
                            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                <TextField
                                    size="small"
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    placeholder="Lọc nhanh tên tài khoản..."
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: 280, "& .MuiInputBase-root": { height: "40px" } }}
                                />
                                {searchFilter && (
                                    <Button 
                                        variant="text" 
                                        onClick={() => setSearchFilter("")}
                                        sx={{ textTransform: "none", color: "#64748b" }}
                                    >
                                        Xóa lọc
                                    </Button>
                                )}
                            </Box>
                        </Card>

                        {/* Data Table Card */}
                        <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)", overflowX: "auto" }}>
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Tên tài khoản</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Số điểm hiện tại</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Tổng điểm nạp</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Hoạt động gần nhất</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155", py: 2 }}>Trạng thái</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((item, idx) => (
                                            <TableRow 
                                                key={item._id}
                                                sx={{ 
                                                    "&:hover": { backgroundColor: "#f8fafc" },
                                                    transition: "background-color 0.15s ease"
                                                }}
                                            >
                                                <TableCell sx={{ py: 1.8, color: "#64748b" }}>
                                                    {(page - 1) * 20 + idx + 1}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <AccountCircleIcon sx={{ color: "#3b82f6", fontSize: 22 }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                                            {item.username}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8 }}>
                                                    <Chip 
                                                        label={`${Math.floor(Number(item.money || 0) / 1000).toLocaleString("vi-VN")} điểm`}
                                                        size="small"
                                                        sx={{ 
                                                            fontWeight: 700, 
                                                            backgroundColor: "#eff6ff", 
                                                            color: "#1d4ed8",
                                                            borderRadius: "6px"
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8, fontWeight: 600, color: "#059669" }}>
                                                    {Math.floor(Number(item.tongnap || 0) / 1000).toLocaleString("vi-VN")}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8, color: "#475569", fontWeight: 500 }}>
                                                    {formatDate(item.lastActiveAt)}
                                                </TableCell>
                                                <TableCell sx={{ py: 1.8 }}>
                                                    <Chip 
                                                        icon={<CircleIcon sx={{ fontSize: "10px !important", color: "#10b981 !important" }} />}
                                                        label="Online" 
                                                        size="small" 
                                                        sx={{ 
                                                            backgroundColor: "#ecfdf5", 
                                                            color: "#047857", 
                                                            fontWeight: 700, 
                                                            border: "1px solid #a7f3d0",
                                                            borderRadius: "6px"
                                                        }} 
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: "15px" }}>
                                                Không có người chơi nào đang online
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Card>

                        {/* Pagination */}
                        <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
                            <Pagination 
                                color="primary" 
                                count={totalPages} 
                                page={page} 
                                shape="rounded"
                                onChange={handleChangePage} 
                                disabled={!onlineUsersSupported} 
                            />
                        </Box>
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default OnlineUsers;
